const STORAGE_PREFIX = "funmiyo_report_modifications";

export function getReportRowKey(row) {
	return `${row.teacherName}\x1f${row.gradeName}`;
}

export function getModificationsStorageKey(schoolId, academicYear) {
	return `${STORAGE_PREFIX}:${schoolId}:${academicYear}`;
}

export function loadReportModifications(schoolId, academicYear) {
	try {
		const raw = localStorage.getItem(
			getModificationsStorageKey(schoolId, academicYear)
		);
		if (!raw) return { excludedKeys: [], groups: [] };
		const parsed = JSON.parse(raw);
		return {
			excludedKeys: Array.isArray(parsed.excludedKeys)
				? parsed.excludedKeys
				: [],
			groups: Array.isArray(parsed.groups) ? parsed.groups : [],
		};
	} catch {
		return { excludedKeys: [], groups: [] };
	}
}

export function saveReportModifications(schoolId, academicYear, modifications) {
	localStorage.setItem(
		getModificationsStorageKey(schoolId, academicYear),
		JSON.stringify({
			excludedKeys: modifications.excludedKeys || [],
			groups: modifications.groups || [],
		})
	);
}

export function mapModificationsToIndices(monthlyRows, { excludedKeys, groups }) {
	const excludedSet = new Set(excludedKeys);
	const excludedRows = new Set();

	monthlyRows.forEach((row, i) => {
		if (excludedSet.has(getReportRowKey(row))) {
			excludedRows.add(i);
		}
	});

	const groupedRows = {};
	let nextGroupId = 1;

	groups.forEach((groupKeys) => {
		const indices = groupKeys
			.filter((key) => !excludedSet.has(key))
			.map((key) =>
				monthlyRows.findIndex((row) => getReportRowKey(row) === key)
			)
			.filter((i) => i >= 0);

		const uniqueIndices = [...new Set(indices)];
		if (uniqueIndices.length >= 2) {
			groupedRows[`group_${nextGroupId}`] = uniqueIndices;
			nextGroupId += 1;
		}
	});

	return { excludedRows, groupedRows, nextGroupId };
}

export function indicesToModifications(monthlyRows, excludedRows, groupedRows) {
	const excludedKeys = Array.from(excludedRows).map((i) =>
		getReportRowKey(monthlyRows[i])
	);
	const groups = Object.values(groupedRows).map((indices) =>
		indices.map((i) => getReportRowKey(monthlyRows[i]))
	);
	return { excludedKeys, groups };
}

export function applyReportModifications(reportData, excludedRows, groupedRows) {
	let filteredMonthly = [...reportData.reportRows.monthly];
	let filteredCumulative = [...reportData.reportRows.cumulative];

	const excludedArray = Array.from(excludedRows).sort((a, b) => b - a);
	excludedArray.forEach((index) => {
		filteredMonthly.splice(index, 1);
		filteredCumulative.splice(index, 1);
	});

	const processedMonthly = [];
	const processedCumulative = [];
	const processedIndices = new Set();

	Object.entries(groupedRows).forEach(([, indices]) => {
		const validIndices = indices.filter((idx) => !excludedRows.has(idx));
		if (validIndices.length === 0) return;

		const adjustedIndices = validIndices.map((idx) => {
			let adjusted = idx;
			excludedArray.forEach((excludedIdx) => {
				if (excludedIdx < idx) adjusted -= 1;
			});
			return adjusted;
		});

		const groupMonthlyRows = adjustedIndices.map((idx) => filteredMonthly[idx]);
		const groupCumulativeRows = adjustedIndices.map(
			(idx) => filteredCumulative[idx]
		);

		const combinedMonthly = {
			schoolName: groupMonthlyRows[0].schoolName,
			gradeName: groupMonthlyRows.map((r) => r.gradeName).join(", "),
			teacherName: groupMonthlyRows.map((r) => r.teacherName).join(", "),
			preGameActivity: groupMonthlyRows.reduce(
				(sum, r) => sum + r.preGameActivity,
				0
			),
			gameRules: groupMonthlyRows.reduce((sum, r) => sum + r.gameRules, 0),
			videosRequired: groupMonthlyRows[0].videosRequired,
			implementationScore: 0,
		};
		combinedMonthly.implementationScore =
			combinedMonthly.videosRequired > 0
				? Math.min(
						100,
						((combinedMonthly.preGameActivity + combinedMonthly.gameRules) /
							combinedMonthly.videosRequired) *
							100
					)
				: 0;

		const combinedCumulative = {
			schoolName: groupCumulativeRows[0].schoolName,
			gradeName: groupCumulativeRows.map((r) => r.gradeName).join(", "),
			teacherName: groupCumulativeRows.map((r) => r.teacherName).join(", "),
			preGameActivity: groupCumulativeRows.reduce(
				(sum, r) => sum + r.preGameActivity,
				0
			),
			gameRules: groupCumulativeRows.reduce(
				(sum, r) => sum + r.gameRules,
				0
			),
			videosRequired: groupCumulativeRows[0].videosRequired,
			implementationScore: 0,
		};
		combinedCumulative.implementationScore =
			combinedCumulative.videosRequired > 0
				? Math.min(
						100,
						((combinedCumulative.preGameActivity +
							combinedCumulative.gameRules) /
							combinedCumulative.videosRequired) *
							100
					)
				: 0;

		processedMonthly.push(combinedMonthly);
		processedCumulative.push(combinedCumulative);
		adjustedIndices.forEach((idx) => processedIndices.add(idx));
	});

	filteredMonthly.forEach((row, index) => {
		if (!processedIndices.has(index)) {
			processedMonthly.push(row);
			processedCumulative.push(filteredCumulative[index]);
		}
	});

	const totalTeachers = processedMonthly.length;
	const totalPreGameActivity = processedMonthly.reduce(
		(sum, row) => sum + row.preGameActivity,
		0
	);
	const totalGameRules = processedMonthly.reduce(
		(sum, row) => sum + row.gameRules,
		0
	);
	const totalVideosRequired = processedMonthly.reduce(
		(sum, row) => sum + row.videosRequired,
		0
	);

	const monthlyScores = processedMonthly
		.map((row) => row.implementationScore)
		.filter((score) => !Number.isNaN(score));
	const avgImplementationScore =
		monthlyScores.length > 0
			? monthlyScores.reduce((sum, score) => sum + score, 0) /
				monthlyScores.length
			: 0;

	const cumulativeScores = processedCumulative
		.map((row) => row.implementationScore)
		.filter((score) => !Number.isNaN(score));
	const avgCumulativeScore =
		cumulativeScores.length > 0
			? cumulativeScores.reduce((sum, score) => sum + score, 0) /
				cumulativeScores.length
			: 0;

	let schoolCategory;
	if (avgCumulativeScore >= 90) {
		schoolCategory = { category: "Excellent", color: "success" };
	} else if (avgCumulativeScore >= 80) {
		schoolCategory = { category: "Good", color: "info" };
	} else if (avgCumulativeScore >= 60) {
		schoolCategory = { category: "Average", color: "warning" };
	} else if (avgCumulativeScore >= 50) {
		schoolCategory = { category: "Below Average", color: "warning" };
	} else {
		schoolCategory = { category: "Poor", color: "error" };
	}

	return {
		...reportData,
		reportRows: {
			monthly: processedMonthly,
			cumulative: processedCumulative,
		},
		schoolScore: avgCumulativeScore,
		currentScore: avgImplementationScore,
		schoolCategory,
		totals: {
			totalTeachers,
			totalPreGameActivity,
			totalGameRules,
			totalVideosRequired,
			avgImplementationScore,
		},
	};
}

function getGroupedIndexSet(groupedRows) {
	return new Set(Object.values(groupedRows).flat());
}

/** Map a visible report row back to original row indices (for selection). */
export function getSourceIndicesForDisplayRow(
	monthlyRow,
	reportRows,
	excludedRows,
	groupedRows
) {
	const groupedIndexSet = getGroupedIndexSet(groupedRows);

	for (const indices of Object.values(groupedRows)) {
		const activeIndices = indices.filter((i) => !excludedRows.has(i));
		if (activeIndices.length < 2) continue;

		const groupTeachers = activeIndices
			.map((i) => reportRows.monthly[i].teacherName.trim())
			.sort()
			.join("|");
		const displayTeachers = monthlyRow.teacherName
			.split(",")
			.map((s) => s.trim())
			.sort()
			.join("|");

		if (groupTeachers === displayTeachers) return activeIndices;
	}

	const idx = reportRows.monthly.findIndex(
		(row, i) =>
			!excludedRows.has(i) &&
			!groupedIndexSet.has(i) &&
			row.teacherName === monthlyRow.teacherName &&
			row.gradeName === monthlyRow.gradeName
	);

	return idx >= 0 ? [idx] : [];
}
